'use client';

import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { isFilled } from "@prismicio/client";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const ProjectCard = ({ project, index }) => {
  const projectData = project?.data;

  if (!isFilled.contentRelationship(project)) return null;

  return (
    <PrismicNextLink
      field={project}
      className="flex flex-col w-full a-no-underline group cursor-pointer"
    >
      {isFilled.image(projectData?.img_mobile) && (
        <div className="w-full lg:hidden overflow-hidden transition-all duration-500  rounded-t-none group-hover:rounded-t-[150%]">
          <PrismicNextImage
            field={{
              ...projectData.img_mobile,
              alt: projectData.img_mobile.alt || "Image du projet mobile"
            }}
            className="w-full h-full max-h-130 object-cover group-hover:scale-102 transition-all duration-700 "
          />
        </div>
      )}

      {isFilled.image(projectData?.img) && (
        <div className="w-full hidden lg:block overflow-hidden transition-all duration-500  rounded-t-none group-hover:rounded-t-[150%]">
          <PrismicNextImage
            field={{
              ...projectData.img,
              alt: projectData.img.alt || "Image du projet"
            }}
            className="w-full h-full max-h-130 object-cover group-hover:scale-102 transition-all duration-700 "
          />
        </div>
      )}
      <div className={`p-8 lg:p-10 pb-12 flex-1 flex flex-col gap-2 ${index == 2 ? '' : 'border-b-[0.5px] md:border-b-0 md:border-r-[0.5px] border-graphite'}`}>
        {isFilled.keyText(projectData?.title) && (
          <h3 className="underline md:no-underline group-hover:underline decoration-green group-hover:decoration-1 underline-offset-6 transition-all">{projectData.title}</h3>
        )}

        {isFilled.keyText(projectData?.Text) && (
          <p className="text-sm italic">{projectData.Text}</p>
        )}
      </div>
    </PrismicNextLink>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectsSlice} ProjectsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectsSlice>} ProjectsProps
 * @type {import("react").FC<ProjectsProps>}
 */
const Projects = ({ slice, context }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [canAnimate, setCanAnimate] = useState(context?.hasSeenIntro !== false);

  // Écouter l'événement de disparition de HomepageIntro
  useEffect(() => {
    const handleIntroHidden = () => {
      if (isInView) {
        setCanAnimate(true);
      }
    };

    window.addEventListener('homepageIntroHidden', handleIntroHidden);
    return () => window.removeEventListener('homepageIntroHidden', handleIntroHidden);
  }, [isInView]);

  // Activer l'animation si l'intro a déjà été vue (cookie présent)
  useEffect(() => {
    if (isInView && context?.hasSeenIntro !== false) {
      setCanAnimate(true);
    }
  }, [isInView, context?.hasSeenIntro]);

  // Animation de typing sur le titre complet
  useEffect(() => {
    if (!canAnimate || isTyping || !isFilled.richText(slice.primary.title)) return;

    const fullText = slice.primary.title[0]?.text;
    if (!fullText) return;

    let timeout;
    let interval;

    // Délai de 300ms avant de commencer l'animation
    timeout = setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;

      interval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setDisplayedText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, 100);
    }, 300);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [canAnimate, slice.primary.title]);

  // Fonction pour reconstruire le texte avec les balises strong
  const renderAnimatedText = () => {
    if (!isFilled.richText(slice.primary.title)) return null;
    
    const titleNode = slice.primary.title[0];
    const fullText = titleNode.text;
    const spans = titleNode.spans || [];
    
    if (spans.length === 0) {
      return displayedText || "";
    }

    const elements = [];
    let lastIndex = 0;

    spans.forEach((span, idx) => {
      if (span.type === 'strong') {
        // Texte avant le strong
        if (span.start > lastIndex) {
          const beforeText = fullText.slice(lastIndex, span.start);
          const visibleBefore = displayedText.slice(lastIndex, Math.min(span.start, displayedText.length));
          if (visibleBefore) {
            elements.push(<span key={`before-${idx}`}>{visibleBefore}</span>);
          }
        }
        
        // Texte du strong
        if (displayedText.length > span.start) {
          const strongText = fullText.slice(span.start, span.end);
          const visibleStrong = displayedText.slice(span.start, Math.min(span.end, displayedText.length));
          if (visibleStrong) {
            elements.push(<strong key={`strong-${idx}`}>{visibleStrong}</strong>);
          }
        }
        
        lastIndex = span.end;
      }
    });

    // Texte après le dernier span
    if (displayedText.length > lastIndex) {
      const afterText = displayedText.slice(lastIndex);
      if (afterText) {
        elements.push(<span key="after">{afterText}</span>);
      }
    }

    return elements.length > 0 ? elements : displayedText || "";
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-20"
    >
      <div className="max-w-400 mx-auto">
        <div className="px-8 md:px-16 max-w-180 mb-20" ref={ref}>
          {isFilled.richText(slice.primary.title) && (
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="h1 mb-4 relative">
                      <span className="invisible">{children}</span>
                      <span className="absolute inset-0">
                        {renderAnimatedText()}
                      </span>
                    </h2>
                  ),
                }}
              />
          )}
          {isFilled.richText(slice.primary.text) && (
            <div className="mb-8 md:mb-10">
              <PrismicRichText
                field={slice.primary.text}
                components={{
                  paragraph: ({ children }) => (
                    <p className="">{children}</p>
                  ),
                }}
              />
            </div>
          )}
        </div>
        {isFilled.group(slice.primary.projects) && (
          <div className="flex flex-col md:flex-row w-full 2xl:px-16">
            {slice.primary.projects.slice(0, 3).map((item, index) => (
              <ProjectCard key={index} project={item.project} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
